// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import {
  DnaSyngoviaGroupNode,
  DnaSyngoviaGroupNodeEType,
} from '../../interfaces/dna-syngovia-group-node';
import {
  DnaSyngoviaNode,
  DnaSyngoviaServerNode,
} from '../../interfaces/dna-syngovia-node';
import { DnaSyngoviaIconService } from '../../services/dna-syngovia-icon.service';
import { DnaSyngoviaScreenInteractionService } from '../../services/dna-syngovia-screen-interaction.service';
import { RouteService } from 'src/app/modules/portal/services/routeService.service';

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-group-node-card',
  templateUrl: './dna-syngovia-group-node-card.component.html',
  styleUrls: ['./dna-syngovia-group-node-card.component.scss'],
})
export class DnaSyngoviaGroupNodeCardComponent
  implements OnChanges, AfterViewInit
{
  @ViewChild('server') server!: TemplateRef<DnaSyngoviaGroupNode>;
  @ViewChild('groupNode') groupNode!: TemplateRef<DnaSyngoviaGroupNode>;

  @Input() selected = false;
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @Input({ required: true }) groupNodeData!: DnaSyngoviaGroupNode;
  @Output() selectedGroupNodeChange = new EventEmitter<DnaSyngoviaGroupNode>();

  protected serverMain: DnaSyngoviaServerNode | undefined;

  protected readonly MASTER = 'master';

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dnaSyngoviaIconService: DnaSyngoviaIconService,
    private readonly screenInteractionService: DnaSyngoviaScreenInteractionService,
    private readonly routeService: RouteService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.initServerMain(changes);
  }

  private initServerMain(changes: SimpleChanges): void {
    if (changes['groupNodeData']) {
      this.serverMain = this.groupNodeData.nodes.find(
        (node) => this.systemDetails.systemSerialNumber === node.serialNumber,
      );
    }
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  protected getTemplateRef(
    groupNodeType: DnaSyngoviaGroupNodeEType
  ): TemplateRef<DnaSyngoviaGroupNode> | undefined {
    let template: TemplateRef<DnaSyngoviaGroupNode> | undefined = undefined;

    switch (groupNodeType) {
      case DnaSyngoviaGroupNodeEType.SERVER:
        template = this.server;
        break;
      case DnaSyngoviaGroupNodeEType.MODALITY:
      case DnaSyngoviaGroupNodeEType.DIGITAL_PRODUCT:
      case DnaSyngoviaGroupNodeEType.CLIENT:
      case DnaSyngoviaGroupNodeEType.DICOM_PROXY:
        template = this.groupNode;
        break;
    }

    return template;
  }

  protected getServerNodes(nodes: DnaSyngoviaNode[]): DnaSyngoviaNode[] {
    return nodes.filter(
      (node) => this.systemDetails.systemSerialNumber !== node.serialNumber
    );
  }

  public onGroupNodeClick(groupNode: DnaSyngoviaGroupNode): void {
    this.selectedGroupNodeChange.emit(groupNode);
  }

  public onHealthcheckClick(): void {
    if (this.serverMain) {
      this.screenInteractionService.serverHealthcheckNext(this.serverMain);
    }
  }

  public onServerItemClick(clickEvent: Event, node: DnaSyngoviaNode): void {
    clickEvent.stopPropagation();

    const { materialNumber, serialNumber } = node;
    if (materialNumber && serialNumber) {
      this.routeService.goToSystem({
        systemMaterialNumber: materialNumber,
        systemSerialNumber: serialNumber,
      });
    }
  }
}
